import Axios from "axios";

import { AndroidWebviewBridge } from "@/tools/AndroidWebviewBridge";
import Bugsnag from "@bugsnag/js";
import { Response } from "@konv/http";
import { Exception, ExceptionCode } from "@konv/exceptions";

export class EcleticaAPI {
  constructor({ apiToken, network, store, baseUrl }) {
    this.apiToken = apiToken;
    this.baseUrl = baseUrl;
    this.network = network;
    this.store = store;

    this.client = Axios.create({
      baseURL: baseUrl,
      timeout: 1000 * 60 * 2,
      // params: {
      //   rede: this.network,
      //   loja: this.store,
      // },
    });
  }

  async doRequest({ endpoint, method, headers, data, params }) {
    try {
      const paramsParams = params ? { params: params } : {};

      const androidBridge = await AndroidWebviewBridge.getInstance();
      if (androidBridge.loaded) {
        const response = await androidBridge.request({
          url: [this.baseUrl, endpoint].join(""),
          method,
          headers: {
            ...headers,
            loginToken: `${this.apiToken}`,
          },
          data,
          query: {
            ...params,
            // rede: this.network,
            // loja: this.store,
          },
        });

        return response;
      }

      const response = await this.client.request({
        url: endpoint,
        method,
        data,
        headers: {
          ...headers,
          loginToken: `${this.apiToken}`,
        },
        ...paramsParams,
      });

      Bugsnag.leaveBreadcrumb(
        "EcleticaAPI - Requisição",
        {
          url: endpoint,
          method,
          data,
          params,
          status: response?.status,
          response: response?.data,
        },
        "request"
      );

      return new Response(response.data, 200, []);
    } catch (err) {
      console.error("EcleticaAPI - Erro na requisição", err.message, err);

      Bugsnag.leaveBreadcrumb(
        "EcleticaAPI - Requisição",
        {
          url: endpoint,
          method,
          data,
          params,
          err: err?.message,
          status: err?.response?.status,
          response: err?.response?.data,
        },
        "request"
      );

      if (err.isAxiosError) {
        const response = err?.response?.data;

        if (!response) {
          return new Response(null, 1000, [`Falha na rede`]);
        }

        const errorMessageSegments = [
          response.errorCode,
          response.errorMessage,
          err.message,
        ];
        const errorMessage =
          `${errorMessageSegments.filter(Boolean).join(" - ")}` ||
          "Erro na comunicação com a Ecletica";

        return new Response(null, 1000, [errorMessage]);
      }

      return new Response(null, 1000, [err.message]);
    }
  }

  async getGroups() {
    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "listaGrupos",
      },
    });

    if (response.getErrors().length > 0) {
      return response;
    }

    const data = response?.getData({}) || {};
    const groups = data.dados_adicionais?.colecao?.grupos || [];
    return new Response(groups, 200, []);
  }

  async getProducts() {
    const response = await this.doRequest({
      endpoint: "/ecletica_ws/services/",
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "listaProdutos",
      },
    });

    if (response.getErrors().length > 0) {
      return response;
    }

    const data = response?.getData({}) || {};
    const products = data.dados_adicionais?.produtos || [];
    return new Response(products, 200, []);
  }

  async getCombinations() {
    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "listaCombos",
      },
    });

    if (response.getErrors().length > 0) {
      return response;
    }

    const data = response?.getData({}) || {};
    const combinations = data.dados_adicionais?.combos || [];
    return new Response(combinations, 200, []);
  }

  async getPizzas() {
    const response = this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "listaPizzas",
      },
    });

    const data = response?.getData({}) || {};

    const pizzas = data.dados_adicionais?.pizzas || {
      configuracao_pizzas: [],
      sabores_pizzas: [],
      opcionais_pizzas: [],
    };

    return new Response(pizzas, 200, []);
  }

  async getProductLinks() {
    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "listaProdutosVinculados",
      },
    });

    if (response.getErrors().length > 0) {
      return response;
    }

    const data = response?.getData({}) || {};
    const productLinks = data.dados_adicionais?.produtos_vinculados || [];
    return new Response(productLinks, 200, []);
  }

  async getUnavailableProducts() {
    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "listaProdutosFaltantes",
      },
    });

    if (response.getErrors().length > 0) {
      return response;
    }

    const data = response.getData({}) || {};
    const unavailableProducts = data.dados_adicionais?.produtos_faltantes || [];

    return new Response(unavailableProducts, 200, []);
  }

  async getTables() {
    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "mapaDeMesas",
      },
    });

    const data = response?.getData({}) || {};
    const tables = data.dados_adicionais?.mesas || [];

    return new Response(tables, 200, []);
  }

  async getTable(tableId) {
    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "consultaTotalConta",
      },
      data: [
        {
          JSON: {
            metodo: "consultarTotalContaJSON",
            tipo_operacao: "mesa",
            numero: tableId,
            numero_conta: 0,
          },
        },
      ],
    });

    if (response.getErrors().length > 0) {
      return response;
    }

    const data = response?.getData({}) || {};

    const tableProducts = data?.dados_adicionais?.produtos || [];
    const productsByGroup = tableProducts.reduce((groups, item) => {
      return {
        ...groups,
        [item.cod_reg]: [...(groups[item.cod_reg] || []), item],
      };
    }, {});

    const sortedItems = Object.values(productsByGroup)
      .flat()
      .sort((a, b) => a - b);

    data.dados_adicionais.produtos = sortedItems;

    const table = data.dados_adicionais;
    return new Response(table, 200, []);
  }

  async getTableItems(tableId) {
    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "consultaItens",
        operacao: "mesa",
        mesa_comanda: tableId,
      },
      data: [
        {
          JSON: {
            metodo: "consultarTotalContaJSON",
            tipo_operacao: "mesa",
            numero: tableId,
            numero_conta: 0,
          },
        },
      ],
    });

    if (response.getErrors().length > 0) {
      return response;
    }

    const data = response.getData({}) || {};

    const tableItems = data.dados_adicionais?.itens || [];

    const productsByGroup = tableItems.reduce((groups, item) => {
      return {
        ...groups,
        [item.cod_reg]: [...(groups[item.cod_reg] || []), item],
      };
    }, {});

    const sortedItems = Object.values(productsByGroup)
      .flat()
      .sort((a, b) => a - b);

    const preparedItems = sortedItems.map((item) => ({
      ...item,
      cod_reg: Number(item.cod_reg),
      cod_item: Number(item.cod_item),
      vlr_unit: Number(item.vlr_unit),
      qtde: Number(item.qtde),
      vlr_total: Number(item.vlr_total),
    }));

    return new Response(preparedItems, 200, []);
  }

  async getTableStatus(tableId) {
    const tablesResponse = await this.getTables();

    if (tablesResponse.getErrors([]).length > 0) {
      return tablesResponse;
    }

    const tables = tablesResponse.getData();
    const table = tables.find((table) => String(table.num) == String(tableId));

    return new Response(table.status, 200, []);
  }

  async addTableItems(tableId, items, transactionId) {
    const payload = [
      {
        JSON: {
          metodo: "inserirPedidoJSON",
          numero: tableId,
          tipo_operacao: "mesa",
          modulo: "Olga",
          numero_pocket: "1",
          versao_atual: "2.3.0",
          pagar_agora: "N",
          usa_pinpad: "N",
          id_transacao: transactionId,
          dadosDispositivo: {
            ip_dispositivo: "127.0.0.1",
            sistema_operacional: "android",
            modelo_pinpad: "",
            operadora_pinpad: "",
          },
          dadosOperador: {
            nome_operador: "Olga",
            codigo_operador: "1",
          },
          inserirPedidoJSON: {
            Itens: items,
            CpfnaNota: [],
            FormaDePagamento: [],
            Pagamento: {
              valor_total: 0,
              dinheiro: 0,
              cheque: 0,
              cartao: 0,
              ticket: 0,
              outros: 0,
              servico: 0,
              desconto: 0,
              vale_ref: 0,
              contra_vale_emi: 0,
              contra_vale_rec: 0,
              troco: 0,
              numero_de_pessoas: 1,
              nome_cliente: "",
              telefone_cliente: "",
              repique: 0,
              pendura: 0,
              cpf: "",
            },
            Checkin: "",
            ComprovantePagamento: [],
          },
        },
      },
    ];

    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "efetuaVenda",
      },
      data: payload,
    });

    return response;
  }

  async preCloseTable(tableId, transactionId) {
    const payload = [
      {
        JSON: {
          metodo: "emitirPreFechamentoJSON",
          numero: tableId,
          tipo_operacao: "mesa",
          modulo: "Olga",
          numero_pocket: "1",
          versao_atual: "2.3.0",
          usa_pinpad: "N",
          id_transacao: transactionId,
          dadosDispositivo: {
            ip_dispositivo: "127.0.0.1",
            sistema_operacional: "android",
            modelo_pinpad: "",
            operadora_pinpad: "",
          },
          dadosOperador: {
            nome_operador: "Olga",
            codigo_operador: "1",
          },
          emitirPreFechamentoJSON: {
            valor_desconto: 0,
            tipo_desconto: "porcentagem",
            numero_pessoas: 1,
          },
        },
      },
    ];

    const response = await this.doRequest({
      endpoint: "/ecletica_ws/services/",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "enviaPreFechamento",
      },
      data: payload,
    });

    return response;
  }

  async callWaiter(tableId) {
    const payload = [
      {
        JSON: {
          metodo: "chamarGarcomJSON",
          numero: tableId,
          tipo_operacao: "mesa",
          nome_cliente: "",
        },
      },
    ];

    const response = await this.doRequest({
      endpoint: `/ecletica_ws/services/`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        metodo: "chamarGarcom",
      },
      data: payload,
    });

    return response;
  }
}
